import type { LineItemInput } from '@faktorplus/shared';
import { Button } from '../../components/ui/button';
import { Input, Select } from '../../components/ui/form';
import { formatAmount } from '../../lib/format';
import styles from './line-items-editor.module.css';

export interface DraftLineItem {
  name: string;
  unit: string;
  quantity: string;
  unitPrice: string;
  vatRate: string;
}

export const EMPTY_LINE_ITEM: DraftLineItem = {
  name: '',
  unit: 'szt',
  quantity: '1',
  unitPrice: '',
  vatRate: '23',
};

const UNITS = ['szt', 'm²', 'm', 'mb', 'kg', 'l', 'kpl', 'opak', 'usł', 'dzień', 'mies.'];
const VAT_RATES = [23, 8, 5, 0];

/**
 * Positions on a manually keyed invoice.
 *
 * Optional on purpose — nobody is going to type three lines off a fuel
 * receipt. When they are filled in, they become the source of the invoice
 * total rather than a second copy of it.
 */
export function LineItemsEditor({
  items,
  onChange,
}: {
  items: DraftLineItem[];
  onChange: (items: DraftLineItem[]) => void;
}) {
  function update(index: number, patch: Partial<DraftLineItem>) {
    onChange(items.map((item, position) => (position === index ? { ...item, ...patch } : item)));
  }

  function remove(index: number) {
    onChange(items.filter((_, position) => position !== index));
  }

  return (
    <div className={styles.editor}>
      {items.length === 0 ? (
        <p className={styles.empty}>
          Brak pozycji — faktura zostanie rozliczona kwotą zbiorczą. To normalne dla paliwa
          czy drobnych zakupów.
        </p>
      ) : (
        <ul className={styles.rows}>
          {items.map((item, index) => (
            <li key={index} className={styles.row}>
              <div className={styles.rowHead}>
                <span className={styles.rowIndex}>{index + 1}.</span>
                <Input
                  aria-label={`Nazwa pozycji ${index + 1}`}
                  placeholder="Nazwa towaru lub usługi"
                  value={item.name}
                  onChange={(event) => update(index, { name: event.target.value })}
                />
                <button
                  type="button"
                  className={styles.remove}
                  onClick={() => remove(index)}
                  aria-label={`Usuń pozycję ${index + 1}`}
                >
                  ✕
                </button>
              </div>

              <div className={styles.rowFields}>
                <Select
                  aria-label={`Jednostka pozycji ${index + 1}`}
                  value={item.unit}
                  onChange={(event) => update(index, { unit: event.target.value })}
                >
                  {UNITS.map((unit) => (
                    <option key={unit} value={unit}>
                      {unit}
                    </option>
                  ))}
                </Select>

                <Input
                  type="number"
                  min="0"
                  step="0.001"
                  aria-label={`Ilość pozycji ${index + 1}`}
                  placeholder="Ilość"
                  value={item.quantity}
                  onChange={(event) => update(index, { quantity: event.target.value })}
                />

                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  aria-label={`Cena netto pozycji ${index + 1}`}
                  placeholder="Cena netto"
                  value={item.unitPrice}
                  onChange={(event) => update(index, { unitPrice: event.target.value })}
                />

                <Select
                  aria-label={`Stawka VAT pozycji ${index + 1}`}
                  value={item.vatRate}
                  onChange={(event) => update(index, { vatRate: event.target.value })}
                >
                  {VAT_RATES.map((rate) => (
                    <option key={rate} value={rate}>
                      {rate}%
                    </option>
                  ))}
                </Select>

                <span className={styles.rowTotal}>{formatAmount(lineNet(item))}</span>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Button onClick={() => onChange([...items, { ...EMPTY_LINE_ITEM }])}>+ Dodaj pozycję</Button>
    </div>
  );
}

export function lineNet(item: DraftLineItem): number {
  return Math.round((Number(item.quantity) || 0) * (Number(item.unitPrice) || 0) * 100) / 100;
}

/** Sum in whole grosz, so the total matches what the API recomputes per line. */
export function sumLineItems(items: DraftLineItem[]): { net: number; vat: number } {
  let netGrosz = 0;
  let vatGrosz = 0;

  for (const item of items) {
    const net = Math.round(lineNet(item) * 100);
    netGrosz += net;
    vatGrosz += Math.round((net * (Number(item.vatRate) || 0)) / 100);
  }

  return { net: netGrosz / 100, vat: vatGrosz / 100 };
}

export function toLineItemInputs(items: DraftLineItem[]): LineItemInput[] {
  return items.map((item) => ({
    name: item.name.trim(),
    unit: item.unit || null,
    quantity: Number(item.quantity) || 0,
    unitPrice: Number(item.unitPrice) || 0,
    vatRate: Number(item.vatRate) || 0,
  }));
}
